/**
 * "Add to Home Screen" — what each platform will actually let us do.
 *
 * The honest summary, because the difference decides the whole UI:
 *
 * | Platform                    | One-tap install? | How |
 * | --------------------------- | ---------------- | --- |
 * | Android Chrome/Edge/Samsung | **yes**          | `beforeinstallprompt` → `prompt()` |
 * | Windows Chrome/Edge         | **yes**          | same event |
 * | macOS Chrome/Edge           | yes              | same event |
 * | **iOS / iPadOS (any browser)** | **no**        | user must use Share → Add to Home Screen |
 * | Firefox (desktop)           | no               | no install concept |
 *
 * **iOS has no install API at all.** Every browser on iOS is Safari's engine,
 * and Apple exposes no programmatic install. Any "one-click install" we claim
 * there would be a lie — so on iOS the CTA opens a short illustrated
 * instruction sheet instead. Pretending otherwise produces a button that does
 * nothing, which is worse than no button.
 *
 * This module is pure and DOM-light so the platform matrix above can be tested
 * without a browser.
 */

export type Platform = 'android' | 'ios' | 'windows' | 'macos' | 'other';

/** What the CTA should do right now. */
export type InstallMode =
  /** A real prompt is queued — one tap installs. */
  | 'prompt'
  /** No API; show the manual Share-sheet steps. */
  | 'instructions'
  /** Already running installed — offer nothing. */
  | 'installed'
  /** This browser cannot install at all — offer nothing. */
  | 'unsupported';

export interface Env {
  userAgent: string;
  /** `navigator.maxTouchPoints`, for iPadOS which lies about its UA. */
  maxTouchPoints?: number;
  /** True when running from the home screen. */
  standalone?: boolean;
  /** True once `beforeinstallprompt` has fired. */
  hasPrompt?: boolean;
}

export function detectPlatform(env: Env): Platform {
  const ua = env.userAgent;

  /* iPadOS 13+ reports a desktop Safari UA, so the only reliable tell is a Mac
     that also has touch. Miss this and every iPad user gets a Chromium install
     button that can never fire. */
  const iPadOS = /Macintosh/.test(ua) && (env.maxTouchPoints ?? 0) > 1;
  if (/iPhone|iPad|iPod/.test(ua) || iPadOS) return 'ios';

  if (/Android/.test(ua)) return 'android';
  if (/Windows/.test(ua)) return 'windows';
  if (/Macintosh|Mac OS X/.test(ua)) return 'macos';
  return 'other';
}

/** True when the page is already running as an installed app. */
export function isStandalone(env: Env): boolean {
  return env.standalone === true;
}

export function installMode(env: Env): InstallMode {
  if (isStandalone(env)) return 'installed';

  /* A queued prompt is proof of installability, whatever the UA says — trust
     the event over sniffing. */
  if (env.hasPrompt) return 'prompt';

  const platform = detectPlatform(env);

  /* iOS can always be added manually, so it is never 'unsupported' — it just
     cannot be automated. */
  if (platform === 'ios') return 'instructions';

  /* Elsewhere, no event means either an already-dismissed install, an
     unsupported browser (Firefox), or a page served over a context that
     disqualifies it. None of those are actionable, so say nothing. */
  return 'unsupported';
}

/** The Share-sheet steps for iOS. Wording matches what Safari actually shows. */
export function iosSteps(): { icon: string; text: string }[] {
  return [
    { icon: '􀈂', text: 'Tap the Share button in the Safari toolbar' },
    { icon: '􀅼', text: 'Scroll down and choose “Add to Home Screen”' },
    { icon: '􀆅', text: 'Tap “Add” — ShweTap appears with your other apps' },
  ];
}

/** Copy for the CTA, per platform. */
export function ctaCopy(mode: InstallMode, platform: Platform): {
  title: string; body: string; action: string;
} | null {
  if (mode === 'installed' || mode === 'unsupported') return null;

  if (mode === 'instructions') {
    return {
      title: 'Add ShweTap to your Home Screen',
      body: 'Full screen, offline-ready, and no App Store needed.',
      action: 'Show me how',
    };
  }

  const where = platform === 'windows' ? 'your desktop'
    : platform === 'android' ? 'your home screen'
      : 'your device';
  return {
    title: 'Install ShweTap',
    body: `Add it to ${where} — full screen, faster launch, works offline.`,
    action: 'Install',
  };
}

/* ── the live browser side ────────────────────────────────────────────────── */

/** Chromium's non-standard event. Not in lib.dom, so declared here. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function readEnv(): Env {
  const nav = globalThis.navigator;
  const standalone =
    globalThis.matchMedia?.('(display-mode: standalone)').matches === true
    /* Safari's own flag, the only way to detect an installed iOS web app. */
    || (nav as unknown as { standalone?: boolean })?.standalone === true;

  return {
    userAgent: nav?.userAgent ?? '',
    maxTouchPoints: nav?.maxTouchPoints ?? 0,
    standalone,
  };
}

const DISMISS_KEY = 'shwetap.install.dismissed';

/**
 * Remember a dismissal for a while.
 *
 * Not forever: a player who says "not now" on day one may well install on day
 * thirty. Not never, either — re-asking every launch is how a good prompt turns
 * into an ad.
 */
export function isDismissed(now: number = Date.now()): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return now - Number(raw) < 14 * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export function markDismissed(now: number = Date.now()): void {
  try { localStorage.setItem(DISMISS_KEY, String(now)); } catch { /* private mode */ }
}

/** Register the service worker. Safe to call on every load. */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  /* Vite dev serves modules that the SW would happily cache and then serve
     stale, which makes HMR behave like a haunting. */
  if (import.meta.env.DEV) return;

  globalThis.addEventListener('load', () => {
    navigator.serviceWorker
      /*
       * `updateViaCache: 'none'` is not optional here.
       *
       * By default the browser may serve the worker SCRIPT from its own HTTP
       * cache, independently of the response headers. A worker that shipped
       * broken therefore keeps being re-fetched from cache and keeps throwing,
       * long after the server is serving a fixed file — which is exactly what
       * happened when a stray `*​/` in a comment stopped sw.js parsing. Bypassing
       * the cache for the script makes the next page load self-healing.
       */
      .register('/sw.js', { updateViaCache: 'none' })
      .then((reg) => {
        /* Ask for a check immediately rather than waiting for the browser's own
           schedule, so a fix lands on this load and not the next one. */
        void reg.update().catch(() => undefined);
      })
      .catch(() => {
        /* An unregistered SW costs offline support, nothing else. Never surface
           it to the player. */
      });
  });
}
